import {
  toShortMarketplace,
  type SeoCenterSort,
  type SeoCenterState,
  type SeoCenterSummary,
  type SeoMarketplace,
  type SeoSeller,
  type SeoTemplateCoverage,
  type SeoTheme,
} from '../seo/api'
import type { ActiveFilter } from '../../components/ui/ActiveFilterChips'
import {
  COLUMNS_LS_KEYS,
  normalizeColumnsOrder,
  normalizeColumnsVisible,
  writeColumnsOrder,
  writeColumnsVisible,
} from './columnsCatalog'
import type {
  SeoCardsCategoryOption,
  SeoCardsColumnKey,
  SeoCardsMode,
  SeoCardsSavedView,
  SeoCardsViewKey,
  SeoCardsViewMeta,
} from './types'

/**
 * Вкладки-виды списка карточек. Набор повторяет выпадающий список
 * «Статус работы» старой страницы плюс «Требуют внимания» из блока
 * приоритетных проблем: ни одно состояние не теряется.
 */
export const SEO_CARDS_VIEWS: readonly SeoCardsViewMeta[] = [
  { key: 'all', label: 'Все карточки', tone: 'gray' },
  { key: 'attention', label: 'Требуют внимания', tone: 'red' },
  { key: 'critical', label: 'Критические ошибки', tone: 'red' },
  { key: 'discrepancy', label: 'Расхождения', tone: 'yellow' },
  { key: 'review', label: 'Требуют проверки', tone: 'yellow' },
  { key: 'draft', label: 'Есть черновик', tone: 'gray' },
  { key: 'ready', label: 'Готово по данным API', tone: 'gray' },
  { key: 'published', label: 'Опубликовано', tone: 'gray' },
  { key: 'new', label: 'Не оценено', tone: 'gray' },
]

/** Вид → значение параметра `state`; для 'all' — null. */
export function viewToStateParam(view: SeoCardsViewKey): SeoCenterState | 'attention' | null {
  return view === 'all' ? null : view
}

/** Значение параметра `state` → вид; null/неизвестное → 'all'. */
export function stateParamToView(state: SeoCenterState | 'attention' | null): SeoCardsViewKey {
  if (!state) return 'all'
  return SEO_CARDS_VIEWS.some((view) => view.key === state) ? state : 'all'
}

/**
 * Счётчики вкладок из сводки кабинета. Без сводки — пустой объект.
 * Формула `attention` повторяет условие бэкенда: state IN
 * ('critical', 'discrepancy', 'review', 'draft').
 */
export function viewCounts(summary?: SeoCenterSummary): Partial<Record<SeoCardsViewKey, number>> {
  if (!summary) return {}
  return {
    all: summary.total,
    attention: summary.critical + summary.discrepancy + summary.review + summary.draft,
    critical: summary.critical,
    discrepancy: summary.discrepancy,
    review: summary.review,
    draft: summary.draft,
    ready: summary.ready,
    published: summary.published,
    new: summary.unassessed,
  }
}

/**
 * «Когда менялось» отвечает на вопрос ротации формулировок, поэтому важна не
 * дата, а давность: точное время оператору не нужно, а «267 дней» — нужно.
 */
export function formatTextAge(value: string | null): { label: string; hint: string | null } {
  if (!value) return { label: 'Не менялось', hint: null }
  const changed = new Date(value)
  if (Number.isNaN(changed.getTime())) return { label: 'Не менялось', hint: null }
  const days = Math.floor((Date.now() - changed.getTime()) / 86_400_000)
  const exact = changed.toLocaleDateString('ru-RU')
  if (days <= 0) return { label: 'Сегодня', hint: exact }
  if (days === 1) return { label: 'Вчера', hint: exact }
  if (days < 30) return { label: `${days} дн. назад`, hint: exact }
  const months = Math.floor(days / 30)
  return { label: `${months} мес. назад`, hint: exact }
}

/** Разряды по-русски: 1 234. */
export function formatNumber(value: number): string {
  return value.toLocaleString('ru-RU')
}

/** Подписи порядков сортировки. */
export const SORT_LABELS: Record<SeoCenterSort, string> = {
  priority: 'Сначала важное',
  stale: 'Давно не менялось',
  recent: 'Недавно менялось',
  potential: 'Больше всего теряем',
}

/** Подписи плотности таблицы. */
export const MODE_LABELS: Record<SeoCardsMode, string> = {
  compact: 'Компакт',
  standard: 'Стандарт',
}

/** Подписи состояний синхронизации; неизвестное значение показывается как есть. */
export const SYNC_STATE_LABELS: Record<string, string> = {
  in_sync: 'Совпадает с площадкой',
  local_ahead: 'Есть неотправленные правки',
  remote_ahead: 'На площадке новее',
  conflict: 'Расхождение',
  unknown: 'Неизвестно',
}

/** Подпись порога давности в фильтре «Давность текста». */
export function staleDayLabel(days: number): string {
  return `Текст не менялся более ${days} дней`
}

/** Подпись пункта категории в фильтрах: «PT1 · Сахарная картинка». */
export function categoryOptionLabel(option: { prefix: string; name: string }): string {
  return option.name ? `${option.prefix} · ${option.name}` : option.prefix
}

/** Подпись пункта тематики в фильтрах: «№ 1394 · Щенячий патруль». */
export function themeOptionLabel(themeNumber: number, themeName: string | null | undefined): string {
  return themeName ? `№ ${themeNumber} · ${themeName}` : `№ ${themeNumber}`
}

/**
 * Справочник категорий для фильтров: уникальные пары префикс → название
 * из карты покрытия. Один префикс встречается в строках разных кабинетов
 * и площадок; пустое название замещается первым непустым.
 */
export function uniqueCategoryOptions(coverage: SeoTemplateCoverage): SeoCardsCategoryOption[] {
  const byPrefix = new Map<string, string>()
  for (const row of coverage.rows) {
    const prefix = (row.category_prefix || '').toUpperCase()
    if (!prefix) continue
    const known = byPrefix.get(prefix)
    if (known === undefined || (known === '' && row.category_name)) {
      byPrefix.set(prefix, row.category_name || '')
    }
  }
  return [...byPrefix.entries()]
    .map(([prefix, name]) => ({ prefix, name }))
    .sort((left, right) => left.prefix.localeCompare(right.prefix, 'ru'))
}

/** Ключи localStorage страницы «Карточки SEO». */
export const LS_KEYS = {
  pageSize: 'seoV2.cards.pageSize',
  columnsOrder: COLUMNS_LS_KEYS.order,
  columnsVisible: COLUMNS_LS_KEYS.visible,
  mode: 'seoV2.cards.mode',
  savedViews: 'seoV2.cards.savedViews',
} as const

const PAGE_SIZE_DEFAULT = 50
const PAGE_SIZE_OPTIONS = [50, 100, 250, 500]

/** Размер страницы из localStorage ('seoV2.cards.pageSize'), по умолчанию 50. */
export function readPageSize(): number {
  const parsed = Number(window.localStorage.getItem(LS_KEYS.pageSize))
  return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : PAGE_SIZE_DEFAULT
}

export function writePageSize(value: number): void {
  window.localStorage.setItem(LS_KEYS.pageSize, String(value))
}

/** Плотность. Незнакомое значение → 'standard'. */
export function readMode(): SeoCardsMode {
  return window.localStorage.getItem(LS_KEYS.mode) === 'compact' ? 'compact' : 'standard'
}

export function writeMode(mode: SeoCardsMode): void {
  window.localStorage.setItem(LS_KEYS.mode, mode)
}

/** Наибольшее число сохранённых видов: лента вкладок не должна уезжать в бесконечность. */
export const SAVED_VIEWS_LIMIT = 20

/** Порядки, которые умеет сохранять вид; повторение набора из `url.ts`. */
const SAVED_VIEW_SORTS: readonly SeoCenterSort[] = ['priority', 'stale', 'recent', 'potential']

/**
 * Разбор одной записи вида. Записи без id или имени отбрасываются;
 * остальные поля нормализуются значениями по умолчанию, чтобы порченая
 * запись не роняла применение вида.
 */
function parseSavedView(raw: unknown): SeoCardsSavedView | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  if (typeof record.id !== 'string' || !record.id) return null
  if (typeof record.name !== 'string' || !record.name) return null

  const filters =
    record.filters && typeof record.filters === 'object'
      ? (record.filters as Record<string, unknown>)
      : {}
  const categories = Array.isArray(filters.categories)
    ? filters.categories.filter((value): value is string => typeof value === 'string')
    : []
  const themes = Array.isArray(filters.themes)
    ? filters.themes.filter(
        (value): value is number => typeof value === 'number' && Number.isInteger(value),
      )
    : []
  const syncStates = Array.isArray(filters.syncStates)
    ? filters.syncStates.filter((value): value is string => typeof value === 'string')
    : []
  const staleDays = typeof filters.staleDays === 'number' ? filters.staleDays : 0

  const columns =
    record.columns && typeof record.columns === 'object'
      ? (record.columns as Record<string, unknown>)
      : {}

  return {
    id: record.id,
    name: record.name,
    view: SEO_CARDS_VIEWS.some((view) => view.key === record.view)
      ? (record.view as SeoCardsViewKey)
      : 'all',
    filters: { categories, themes, syncStates, staleDays },
    sort: SAVED_VIEW_SORTS.includes(record.sort as SeoCenterSort)
      ? (record.sort as SeoCenterSort)
      : 'priority',
    columns: {
      order: normalizeColumnsOrder(columns.order),
      visible: [...normalizeColumnsVisible(columns.visible)],
    },
    mode: record.mode === 'compact' ? 'compact' : 'standard',
  }
}

/** Виды из хранилища. Мусор, записи без id или имени отбрасываются. */
export function readSavedViews(): SeoCardsSavedView[] {
  const raw = window.localStorage.getItem(LS_KEYS.savedViews)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map(parseSavedView)
      .filter((view): view is SeoCardsSavedView => view !== null)
  } catch {
    return []
  }
}

export function writeSavedViews(views: SeoCardsSavedView[]): void {
  window.localStorage.setItem(LS_KEYS.savedViews, JSON.stringify(views))
}

const TRANSLIT_MAP: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh',
  щ: 'shch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
}

/** Кириллица → латиница; неизвестные символы остаются как есть. */
function transliterate(value: string): string {
  let result = ''
  for (const char of value.toLowerCase()) {
    result += TRANSLIT_MAP[char] ?? char
  }
  return result
}

/**
 * Идентификатор из имени: транслитерация в латиницу, нижний регистр,
 * не-буквенно-цифровые — в дефис. При совпадении с существующим добавляется
 * «-2», «-3» и далее. Пустое имя даёт 'view'.
 *
 * Считается детерминированно, без Date.now() и Math.random(): иначе
 * идентификатор нельзя проверить тестом, а ссылка на вид перестаёт быть
 * воспроизводимой.
 */
export function makeViewId(name: string, existing: SeoCardsSavedView[]): string {
  const base =
    transliterate(name.trim())
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'view'
  const ids = new Set(existing.map((view) => view.id))
  if (!ids.has(base)) return base
  let suffix = 2
  while (ids.has(`${base}-${suffix}`)) suffix += 1
  return `${base}-${suffix}`
}

/** Текущая настройка страницы — срез для создания сохранённого вида. */
export interface SeoCardsSettingsSnapshot {
  view: SeoCardsViewKey
  categories: string[]
  themes: number[]
  syncStates: string[]
  staleDays: number
  sort: SeoCenterSort
  order: SeoCardsColumnKey[]
  visible: SeoCardsColumnKey[]
  mode: SeoCardsMode
}

/**
 * Новый сохранённый вид поверх существующих. При достижении
 * SAVED_VIEWS_LIMIT возвращает null — страница показывает тост.
 */
export function createSavedView(
  name: string,
  existing: SeoCardsSavedView[],
  current: SeoCardsSettingsSnapshot,
): SeoCardsSavedView | null {
  if (existing.length >= SAVED_VIEWS_LIMIT) return null
  return {
    id: makeViewId(name, existing),
    name,
    view: current.view,
    filters: {
      categories: current.categories,
      themes: current.themes,
      syncStates: current.syncStates,
      staleDays: current.staleDays,
    },
    sort: current.sort,
    columns: { order: current.order, visible: current.visible },
    mode: current.mode,
  }
}

/**
 * Создаёт вид и кладёт его в хранилище. null — достигнут SAVED_VIEWS_LIMIT.
 */
export function appendSavedView(
  name: string,
  existing: SeoCardsSavedView[],
  current: SeoCardsSettingsSnapshot,
): { views: SeoCardsSavedView[]; created: SeoCardsSavedView } | null {
  const created = createSavedView(name, existing, current)
  if (!created) return null
  const views = [...existing, created]
  writeSavedViews(views)
  return { views, created }
}

/**
 * Кабинеты площадки и выбранный кабинет: запрошенный в адресе, если он
 * среди кабинетов площадки, иначе первый доступный.
 */
export function pickSeller(
  sellers: SeoSeller[],
  marketplace: SeoMarketplace,
  requestedSellerId: number | null,
): { visibleSellers: SeoSeller[]; sellerId: number | null } {
  const visibleSellers = sellers.filter(
    (seller) => toShortMarketplace(seller.marketplace) === marketplace,
  )
  const sellerId = visibleSellers.some((seller) => seller.seller_id === requestedSellerId)
    ? requestedSellerId
    : visibleSellers[0]?.seller_id ?? null
  return { visibleSellers, sellerId }
}

/**
 * Применение вида к настройке рабочего места: порядок, видимость столбцов
 * и плотность записываются в хранилище и возвращаются для состояния страницы.
 */
export function applySavedViewSettings(view: SeoCardsSavedView): {
  order: SeoCardsColumnKey[]
  visible: Set<SeoCardsColumnKey>
  mode: SeoCardsMode
} {
  writeColumnsOrder(view.columns.order)
  const visible = new Set(view.columns.visible)
  writeColumnsVisible(visible)
  writeMode(view.mode)
  return { order: view.columns.order, visible, mode: view.mode }
}

/** Что попадёт в сохранённый вид — перечень для диалога сохранения. */
export function savedViewSummary(current: {
  view: SeoCardsViewKey
  categories: string[]
  themes: number[]
  syncStates: string[]
  staleDays: number
  sort: SeoCenterSort
  visibleColumns: number
  mode: SeoCardsMode
}): string[] {
  const viewLabel = SEO_CARDS_VIEWS.find((view) => view.key === current.view)?.label ?? ''
  const syncLabels = current.syncStates.map((value) => SYNC_STATE_LABELS[value] ?? value)
  return [
    `Вкладка: ${viewLabel}`,
    current.categories.length ? `Категории: ${current.categories.length}` : 'Категории: не выбраны',
    current.themes.length ? `Тематики: ${current.themes.length}` : 'Тематики: не выбраны',
    syncLabels.length ? `Синхронизация: ${syncLabels.join(', ')}` : 'Синхронизация: не выбрана',
    current.staleDays ? `Давность: ${staleDayLabel(current.staleDays)}` : 'Давность: любая',
    `Порядок: ${SORT_LABELS[current.sort]}`,
    `Столбцов показано: ${current.visibleColumns}`,
    `Плотность: ${MODE_LABELS[current.mode]}`,
  ]
}

/**
 * Чипы активных фильтров. Колбэки удаления зовёт страница — им нужен адрес.
 */
export function buildFilterChips(args: {
  categories: string[]
  themes: number[]
  syncStates: string[]
  staleDays: number
  categoryOptions: SeoCardsCategoryOption[]
  themeOptions: SeoTheme[]
  onRemoveCategory: (prefix: string) => void
  onRemoveTheme: (themeNumber: number) => void
  onRemoveSync: (value: string) => void
  onRemoveStale: () => void
}): ActiveFilter[] {
  const list: ActiveFilter[] = []
  for (const prefix of args.categories) {
    const option = args.categoryOptions.find((item) => item.prefix === prefix)
    list.push({
      key: `cat:${prefix}`,
      prefix: 'Категория',
      label: categoryOptionLabel(option ?? { prefix, name: '' }),
      onRemove: () => args.onRemoveCategory(prefix),
    })
  }
  for (const themeNumber of args.themes) {
    const themeName = args.themeOptions.find((option) => option.theme_number === themeNumber)?.theme_name
    list.push({
      key: `theme:${themeNumber}`,
      prefix: 'Тематика',
      label: themeOptionLabel(themeNumber, themeName),
      onRemove: () => args.onRemoveTheme(themeNumber),
    })
  }
  for (const value of args.syncStates) {
    list.push({
      key: `sync:${value}`,
      prefix: 'Синхронизация',
      label: SYNC_STATE_LABELS[value] ?? value,
      onRemove: () => args.onRemoveSync(value),
    })
  }
  if (args.staleDays) {
    list.push({
      key: 'stale',
      prefix: 'Давность',
      label: `более ${args.staleDays} дн.`,
      onRemove: args.onRemoveStale,
    })
  }
  return list
}

/**
 * Справочник категорий для фильтров: карта покрытия, а при её недоступности —
 * префиксы из фасетов без названий.
 */
export function categoryOptionsWithFallback(
  coverageOptions: SeoCardsCategoryOption[] | null,
  facets: Record<string, number> | null,
): SeoCardsCategoryOption[] {
  if (coverageOptions?.length) return coverageOptions
  if (facets) return Object.keys(facets).map((prefix) => ({ prefix, name: '' }))
  return []
}

/** Сообщение об ошибке из ответа API или из самого Error. */
export function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as {
    response?: { data?: { error?: { message?: string }; message?: string } }
    message?: string
  }
  return (
    candidate.response?.data?.error?.message ||
    candidate.response?.data?.message ||
    candidate.message ||
    fallback
  )
}
